import React, { useState } from 'react';

const TABS = [
  { key: 'payload', label: "What You'll Receive" },
  { key: 'integrations', label: 'Popular Integrations' },
  { key: 'signatures', label: 'Verify Signatures' },
];

const WebhookSetupGuideModal = ({ isOpen, onClose, webhookSecret, onRevealSecret, loadingSecret, onCopySecret, secretCopied }) => {
  const [activeTab, setActiveTab] = useState('payload');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200/80 dark:border-gray-800 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">Setup Guide</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">How to use webhook data</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-gray-800/80 px-6 bg-white dark:bg-gray-900">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 dark:bg-gray-900/50">
          {activeTab === 'payload' && <PayloadTab />}
          {activeTab === 'integrations' && <IntegrationsTab />}
          {activeTab === 'signatures' && (
            <SignaturesTab
              webhookSecret={webhookSecret}
              onRevealSecret={onRevealSecret}
              loadingSecret={loadingSecret}
              onCopySecret={onCopySecret}
              secretCopied={secretCopied}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ────────────────── Tab: What You'll Receive ────────────────── */

const PayloadTab = () => (
  <div className="space-y-5">
    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
      When an automation finishes running, we send a <code className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">POST</code> request to your webhook URL with a JSON payload containing the results.
    </p>

    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Example Payload</h4>
      <pre className="text-2xs bg-white dark:bg-gray-800/60 rounded-xl p-4 overflow-x-auto text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700/50 leading-relaxed">{`{
  "event": "export.completed",
  "timestamp": "2026-02-17T12:00:00.000Z",
  "data": {
    "task_name": "Product Listings",
    "task_id": "550e8400-e29b-41d4-a716-446655440000",
    "task_run_id": "7a1b2c3d-e4f5-6789-abcd-ef0123456789",
    "table_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "download_url": "https://exports.ultimatewebscraper.com/exports/ws_8f3a.../tbl_a1b2.../1740825600000.csv",
    "format": "csv",
    "row_count": 1500
  }
}`}</pre>
    </div>

    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Field Reference</h4>
      <div className="space-y-2">
        {[
          ['event', 'Always "export.completed" for automation runs'],
          ['timestamp', 'ISO 8601 timestamp of when the export finished'],
          ['data.task_name', 'The name of the automation that ran'],
          ['data.task_id', 'Unique ID of the automation'],
          ['data.task_run_id', 'Unique ID of this specific run'],
          ['data.table_id', 'Unique ID of the results table'],
          ['data.download_url', 'Direct link to download results in your chosen format'],
          ['data.format', '"csv" or "json" (configurable in integration settings)'],
          ['data.row_count', 'Number of rows scraped in this run'],
        ].map(([field, desc]) => (
          <div key={field} className="flex gap-3 text-sm">
            <code className="text-2xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-indigo-600 dark:text-indigo-400 font-mono flex-shrink-0 h-fit">{field}</code>
            <span className="text-gray-600 dark:text-gray-400 text-xs">{desc}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ────────────────── Tab: Popular Integrations ────────────────── */

const IntegrationCard = ({ icon, name, color, steps }) => (
  <div className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4">
    <div className="flex items-center gap-2.5 mb-3">
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center text-white text-sm font-bold`}>
        {icon}
      </div>
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{name}</h4>
    </div>
    <ol className="space-y-2">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-2.5 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
          <span className="text-indigo-500 dark:text-indigo-400 font-semibold flex-shrink-0 mt-px">{i + 1}.</span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

const IntegrationsTab = () => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
      Connect your scraped data to thousands of apps. Copy the webhook URL from the service below and paste it in the integrations page.
    </p>

    <div className="grid grid-cols-1 gap-3">
      <IntegrationCard
        icon="Z"
        name="Zapier"
        color="bg-[#FF4A00]"
        steps={[
          'Create a new Zap and choose "Webhooks by Zapier" as the trigger',
          'Select "Catch Hook" as the trigger event',
          'Copy the webhook URL Zapier gives you',
          'Paste the URL in the Integrations page and click Connect',
          'Click "Send Test" to verify — then continue building your Zap',
        ]}
      />

      <IntegrationCard
        icon="M"
        name="Make (Integromat)"
        color="bg-[#6D00CC]"
        steps={[
          'Create a new scenario and add a "Webhooks" module',
          'Choose "Custom webhook" and create a new webhook',
          'Copy the URL Make provides',
          'Paste the URL in the Integrations page and click Connect',
          'Click "Send Test" to verify — Make will detect the data structure automatically',
        ]}
      />

      <IntegrationCard
        icon="n8n"
        name="n8n"
        color="bg-[#EA4B71]"
        steps={[
          'Add a "Webhook" trigger node to your workflow',
          'Set the HTTP Method to POST',
          'Copy the Production URL from the node settings',
          'Paste the URL in the Integrations page and click Connect',
          'Activate your workflow, then click "Send Test" to verify',
        ]}
      />

      <IntegrationCard
        icon="<>"
        name="Custom Server"
        color="bg-gray-700 dark:bg-gray-600"
        steps={[
          'Set up a POST endpoint on your server that accepts JSON',
          'Parse the payload to get the download_url and task metadata',
          'Download the file from download_url (CSV or JSON depending on your settings)',
          'Process the data as needed — import to database, send emails, etc.',
          '(Optional) Verify the X-Webhook-Signature header for security',
        ]}
      />
    </div>
  </div>
);

/* ────────────────── Tab: Verify Signatures ────────────────── */

const SignaturesTab = ({ webhookSecret, onRevealSecret, loadingSecret, onCopySecret, secretCopied }) => (
  <div className="space-y-5">
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        Each webhook request includes an <code className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">X-Webhook-Signature</code> header.
        You can use this to verify the payload came from us and hasn't been tampered with. This step is optional but recommended for production use.
      </p>
    </div>

    {/* Signing secret */}
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Your Signing Secret</h4>
      <div className="flex items-center gap-2">
        {webhookSecret ? (
          <>
            <code className="flex-1 text-xs bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-lg px-3 py-2 font-mono text-gray-700 dark:text-gray-300 break-all select-all">
              {webhookSecret}
            </code>
            <button
              onClick={() => onCopySecret(webhookSecret)}
              className="flex-shrink-0 px-2.5 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              {secretCopied ? 'Copied!' : 'Copy'}
            </button>
          </>
        ) : (
          <button
            onClick={onRevealSecret}
            disabled={loadingSecret}
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 disabled:opacity-50"
          >
            {loadingSecret ? 'Loading...' : 'Show signing secret'}
          </button>
        )}
      </div>
    </div>

    {/* Node.js example */}
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Node.js</h4>
      <pre className="text-2xs bg-white dark:bg-gray-800/60 rounded-xl p-4 overflow-x-auto text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700/50 leading-relaxed">{`const crypto = require('crypto');

function verifyWebhook(body, signature, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(body)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// In your handler:
const sig = req.headers['x-webhook-signature'];
const isValid = verifyWebhook(req.rawBody, sig, SECRET);`}</pre>
    </div>

    {/* Python example */}
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Python</h4>
      <pre className="text-2xs bg-white dark:bg-gray-800/60 rounded-xl p-4 overflow-x-auto text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700/50 leading-relaxed">{`import hmac, hashlib

def verify_webhook(body: bytes, signature: str, secret: str) -> bool:
    expected = 'sha256=' + hmac.new(
        secret.encode(), body, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)

# In your handler:
sig = request.headers.get('X-Webhook-Signature')
is_valid = verify_webhook(request.data, sig, SECRET)`}</pre>
    </div>
  </div>
);

export default WebhookSetupGuideModal;
